import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AsyncImage from "../../Requirement/components/common/AsyncImage";
import {
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  TextField,
  Box,
  Typography,
  Button,
  Collapse,
  TextareaAutosize,
} from "@mui/material";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
  uploadVideoToS3,
} from "../../Requirement/services/RequirementServices";
import debounce from "lodash/debounce";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OfferDetails from "../requirement/component/OfferDetails";
import ProductOffers from "./components/ProductOffers";

const BrouseProducts = () => {
  const { setLoading } = useLoading();
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [totalItems, setTotalItems] = useState(0);
  const [products, setProducts] = useState([]);

  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    deliveryDate: "",
    requirementDate: "",
    status: "",
    searchCategory: "",
    searchProduct: "",
    searchGrade: "",
    searchPriceRange: "",
  });
  const [selectedProduct, setSelectedProduct] = useState(null);

  const priceRangeOptions = [
    { value: "", label: "Select Range" },
    { value: "50-100", label: "50-100 MT" },
    { value: "100-200", label: "100-200 MT" },
    { value: "200-500", label: "200-500 MT" },
    { value: "Above-500", label: "Above 500 MT" },
  ];


  const loadProductCategoryList = useCallback(async () => {
    setLoading(true);
    try {
      const initialResponse = await fetchData({
        target: "listProductCategory",
        userOrder: "CategoryName",
        rowStart: 0,
        pageSize: 100,
      });

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const allItems = await searchfetchQueries({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: totalCount,
        });

        setProductCategoryList(allItems.items || allItems);
      } else {
        setProductCategoryList([]);
      }
    } catch (error) {
      console.error("Error loading listProductCategory: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadGradeList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 100,
        filterTerms: {},
      });

      const products = response.items || response;
      const uniqueGrades = [
        ...new Set(products.map((product) => product.Grade).filter(Boolean)),
      ].map((grade) => ({
        value: grade,
        label: grade,
      }));

      setGradeList(uniqueGrades);
    } catch (error) {
      console.error("Error loading grades: ", error);
      setGradeList([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async () => {
    setLoading(true);
    try {
      const initialResponse = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 100,
        filterTerms: {Status: "ACTIVE"},
      });

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const allItems = await searchfetchQueries({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: totalCount,
          filterTerms: {},
        });

        const products = allItems.items || allItems;

        setProductList(products);
      } else {
        setProductList([{ value: "", label: "Select Product" }]);
      }
    } catch (error) {
      console.error("Error loading product list: ", error);
      setProductList([{ value: "", label: "Select Product" }]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const searchProduct = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      try {
        let priceFilter = {};
        if (searchValue.searchPriceRange) {
          if (searchValue.searchPriceRange === "Above-500") {
            priceFilter = { PriceMin: 500 };
          } else {
            const [min, max] = searchValue.searchPriceRange
              .split("-")
              .map((val) => parseFloat(val));
            priceFilter = { PriceMin: min, PriceMax: max };
          }
        }

        const body = {
          target: "listProductsWithOfferCountVw",
          userOrder: "ProductName ASC",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            Status:"ACTIVE",
            ProductNamePartial: searchValue.searchProduct || "",
            ProductCategoryID: searchValue.searchCategory || "",
            Grade: searchValue.searchGrade || "",
            ...priceFilter,
          },
        };

        const response = await fetchData(body);
        setProducts(response.items || response);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading listProducts: ", error);
        setProducts([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading]
  );



  const debouncedSearchProduct = useCallback(
    debounce((searchValue, newRowStart) => {
      searchProduct(searchValue, newRowStart);
    }, 500),
    [searchProduct]
  );

  useEffect(() => {
    loadProductCategoryList();
    loadGradeList();
    loadProductList();
    searchProduct(searchValues, 0);
    setSelectedProduct(null);
  }, [loadProductCategoryList, loadGradeList, loadProductList, searchProduct]);

  const fields = [
    {
      name: "searchCategory",
      label: "Category",
      type: "dropdown",
      options: productCategoryList.map((item) => ({
        value: item.ProductCategoryID,
        label: item.CategoryName,
      })),
      value: searchValues.searchCategory,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchCategory: value,
          searchProduct: "", // Reset product when category changes
          searchGrade: "", // Reset grade when category changes
        }));
        searchProduct(
          {
            ...searchValues,
            searchCategory: value,
            searchProduct: "",
            searchGrade: "",
          },
          0
        );
      },
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: [
        { value: "", label: "Select Product" },
        ...[
          ...new Set(
            productList
              .filter(
                (product) =>
                  !searchValues.searchCategory ||
                  product.ProductCategoryID === searchValues.searchCategory
              )
              .map((product) => product.ProductName)
          ),
        ].map((product) => ({
          value: product,
          label: product,
        })),
      ],
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          searchProduct: value,
          searchGrade: "", // Reset grade when product changes
        }));
        searchProduct(
          { ...searchValues, searchProduct: value, searchGrade: "" },
          0
        );
      },
    },
    {
      name: "searchGrade",
      label: "Grade",
      type: "dropdown",
      options: [
        { value: "", label: "Select Grade" },
        ...productList
          .filter(
            (product) =>
              product.ProductName === searchValues.searchProduct &&
              (!searchValues.searchCategory ||
                product.ProductCategoryID === searchValues.searchCategory)
          )
          .map((product) => ({
            value: product.Grade,
            label: product.Grade,
          })),
      ],
      value: searchValues.searchGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchGrade: value }));
        searchProduct({ ...searchValues, searchGrade: value }, 0);
      },
      disabled: !searchValues.searchProduct,
    },
    {
      name: "searchPriceRange",
      label: "Price Range ($/MT)",
      type: "dropdown",
      options: priceRangeOptions,
      value: searchValues.searchPriceRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchPriceRange: value }));
        searchProduct({ ...searchValues, searchPriceRange: value }, 0);
      },
    },
  ];

  const handleSearch = (searchValues) => {
    setRowStart(0);
    searchProduct(searchValues, 0);
  };

  const handlePageChange = (event, newPage) => {
    const newRowStart = (newPage - 1) * pageSize;
    setRowStart(newRowStart);
    searchProduct(searchValues, newRowStart);
  };

  const handleViewOffers = useCallback((product) => {
    setSelectedProduct((prev) =>
      prev?.ProductID === product.ProductID ? null : product
    );
  }, []);



  const handleApprove = () => {
    console.log("Approve clicked");
  };

  // Ensure ProductOffers re-renders when selectedProduct changes
  useEffect(() => {
    if (selectedProduct) {
      console.log("Selected product updated:", selectedProduct);
    }
  }, [selectedProduct]);

  const pageCount = Math.ceil(totalItems / pageSize);
  const currentPage = Math.floor(rowStart / pageSize) + 1;

  return (
    <div className="bowseProduct">
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Browse Products</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>

      </div>
      <div className="row main-row">
        {products.map((product) => (
          <div
            className="margin-botton-5 col-12 col-sm-6 col-md-3 col-sx-12"
            key={product.ProductID}
          >
            <div className="card product-card d-flex flex-row">
              <div className="image-css">
                <AsyncImage
                  imageKey={product.ProductImagePath}
                  alt={product.ProductName || "Product Image"}
                  className="product-image"
                  width="100%"
                  height="100px"
                  objectFit="unset"
                  style={{ borderRadius: "5px", border: "1px solid #cccccc61" }}
                />
                <p
                  className="view-offers text-mute"
                  style={{ cursor: "pointer", color: "#de6b14" }}
                  onClick={() => handleViewOffers(product)}
                >
                  {selectedProduct?.ProductID === product.ProductID
                    ? "Hide Offers"
                    : "View Offers"}
                </p>
              </div>
              <div className="card-body d-flex flex-column justify-content-between">
                <div>
                  <h5 className="card-title">{product.ProductName}</h5>
                  <p className="card-text grade">{product.Grade || "N/A"}</p>
                  <p className="card-text origin">
                    {product.Origin || "India"}
                  </p>
                  <p className="card-text price bold">
                    {" "}
                    ${product.PriceMin}
                    {product.PriceMax ? ` - ${product.PriceMax}` : ""} / MT
                  </p>
                  <p
                    className="card-text2 bold"
                    style={{ color: "#de6b14 !important" }}
                  >
                    {product.TotalOffers} Offers
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {totalItems > 0 && (
        <div className="pagination mt-6 flex justify-center">
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            sx={{
              "& .MuiPaginationItem-root": {
                backgroundColor: "#f0f0f0",
                "&.Mui-selected": {
                  backgroundColor: "#de6b14",
                  color: "#fff",
                },
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                  color: "#de6b14",
                },
              },
            }}
          />
        </div>
      )}
      {selectedProduct && (
        <ProductOffers key={selectedProduct.ProductID} product={selectedProduct} />
      )}
    </div>
  );
};

export default BrouseProducts;
